import React, { useState, useEffect, Key } from 'react';
import { useNavigate} from 'react-router-dom';
import {  Select, SelectItem, CircularProgress } from "@heroui/react";
import { getRecords, RecordKind, Gender } from '../api/api';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
} from "@heroui/react";
import { RecordKindIcon } from './RecordKindIcon';

interface RecordData {
  id: string;
  record_kind: RecordKind;
  alias: string;
  age_range: string;
  gender: Gender | '';
  last_updated: string;
}

const RecordsList: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);   
  const [records, setRecords] = useState<RecordData[]>([]);

  const navigate = useNavigate();

  const [selectedDateRange, setSelectedDateRange] = useState<string>('lastweek');

  const [page, setPage] = React.useState(1);
  const rowsPerPage = 12;
  
  // Calculate the total number of pages based on the length of the records
  const pages = Math.ceil(records.length / rowsPerPage);
  
  // Slice the records array to get the current page's items
  const items = React.useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
  
    return records.slice(start, end);
  }, [page, records]);


  const fetchRecords = async () => {

    try {
      setIsLoading(true)
      const responseData = await getRecords(selectedDateRange);

      console.log(responseData.length);
      const sortedRecords = responseData.sort(
        (a: RecordData, b: RecordData) => new Date(b.last_updated).getTime() - new Date(a.last_updated).getTime()
      );
      console.log("Sorted Records")
      console.log(sortedRecords.length);
      setRecords(sortedRecords);
    } catch (error) {
      console.error("Error fetching records:", error);
    } finally {
      setIsLoading(false); // Ensure loading is false after API call
    }
  };

  const classNames = React.useMemo(
    () => ({
      th: ["bg-transparent", "bg-primary", "text-white"],
    }),
    [],
  );

  const formatItemInfo = (item: RecordData) => {
    const parts = [];
    if (item.age_range){
      parts.push(item.age_range)
    }
    if(item.gender){
      parts.push(item.gender.charAt(0).toUpperCase() + item.gender.slice(1).toLowerCase())
    }
    return parts.join(" ")
  }
    
  useEffect(() => {
    fetchRecords();
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [selectedDateRange]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
          {isLoading && (
                <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000,
                }}
                >
                    <CircularProgress label="Loading..." />
                </div>
          )}
          <main className="flex-1 container max-w-(--breakpoint-lg) mx-auto bg-white">
            <Select
                            className='py-1'
                            label="Date Range"
                            placeholder="Select a range"
                            value={selectedDateRange}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedDateRange(e.target.value)}
                            defaultSelectedKeys={[selectedDateRange]}
                        >
                            <SelectItem key="lastweek" textValue="Last Week">Last Week</SelectItem>
                            <SelectItem key="lastmonth" textValue="Last Month">Last Month</SelectItem>
                            <SelectItem key="last3months" textValue="Last 3 Months">Last 3 Months</SelectItem>
                            <SelectItem key="last6months" textValue="Last 6 Months">Last 6 Months</SelectItem>
                            <SelectItem key="last12months" textValue="Last 12 Months">Last 12 Months</SelectItem>
            </Select>
            <Table
              aria-label="Records Table"
              classNames={classNames}
              bottomContent={
                <div className="flex w-full justify-center">
                  <Pagination
                    showControls
                    showShadow
                    color="primary"
                    page={page}
                    total={pages}
                    onChange={(page) => setPage(page)}
                  />
                </div>
              }
              isStriped
              shadow="none"
              onRowAction={(key: Key) => {              
                // Find the record by the key
                const record = records.find((record) => record.id === key);
              
                if (record) {
                  // Navigate to the record's page
                  navigate(`/record/${record.id}`);
                } else {
                  // Log an error if the record isn't found
                  console.error("No record found for the given key:", key);
                }
              }}
            >
              <TableHeader>
                <TableColumn key="name">Alias</TableColumn>
                <TableColumn key="role">Info</TableColumn>
                <TableColumn key="date">Last Updated</TableColumn>
              </TableHeader>
              <TableBody items={items}>
                {(item) => (
                  <TableRow key={item.id} className='cursor-pointer'>
                    <TableCell>
                      <RecordKindIcon recordKind={item.record_kind}/>{item.alias}</TableCell>
                    <TableCell>{formatItemInfo(item)}</TableCell>
                    <TableCell>{new Date(item.last_updated).toLocaleString("en-GB")}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </main>
    </div>
  );
};

export default RecordsList;
